import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useSaveColumnMappings, useValidateCsv } from "@/hooks/use-import";
import { SAMPLE_ROW_COUNT, sampleCsvFile } from "@/lib/csv-sample";
import type {
  CsvValidationResponse,
  CustomFieldDefinition,
  ImportEntityType,
} from "@adva/types";
import { Button } from "@/components/ui/button";
import { ColumnMapper } from "@/pages/v2/import/components/column-mapper";
import { StepCounter } from "../components/step-counter";
import type { ColumnMappingOverride } from "../state";

interface StepMapColumnsProps {
  entityType: ImportEntityType;
  file: File | null;
  validation: CsvValidationResponse;
  overrides: Record<string, ColumnMappingOverride>;
  onSetMapping: (override: ColumnMappingOverride) => void;
  onClearMapping: (source_header: string) => void;
  onSetValidation: (validation: CsvValidationResponse) => void;
  onBack: () => void;
  onContinue: () => void;
  onError: (message: string) => void;
}

export function StepMapColumns({
  entityType,
  file,
  validation,
  overrides,
  onSetMapping,
  onClearMapping,
  onSetValidation,
  onBack,
  onContinue,
  onError,
}: StepMapColumnsProps) {
  const { column_mapping } = validation;
  const validateCsv = useValidateCsv();
  const saveColumnMappings = useSaveColumnMappings();
  const [saving, setSaving] = useState(false);

  const hasBlockingErrors = column_mapping.missing_required.length > 0;

  const handleFieldCreated = async (_definition: CustomFieldDefinition) => {
    if (!file) return;
    try {
      const sampled = await sampleCsvFile(file, SAMPLE_ROW_COUNT);
      const fresh = await validateCsv.mutateAsync({
        entityType,
        file: sampled,
      });
      onSetValidation(fresh);
    } catch (err) {
      onError(
        err instanceof Error
          ? err.message
          : "Failed to re-validate after field creation",
      );
    }
  };

  const handleContinue = async () => {
    const mappings = Object.values(overrides);
    if (mappings.length > 0) {
      setSaving(true);
      try {
        await saveColumnMappings.mutateAsync({ entityType, mappings });
      } catch (err) {
        onError(
          err instanceof Error ? err.message : "Failed to save column mappings",
        );
        setSaving(false);
        return;
      }
      setSaving(false);
    }
    onContinue();
  };

  return (
    <div className="space-y-5">
      <StepCounter current={2} total={4} label="Map columns" />

      {column_mapping.missing_required.length > 0 && (
        <div className="rounded-lg border border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/5 p-4">
          <p className="text-sm font-semibold text-[color:var(--color-danger)] mb-2">
            Missing required columns
          </p>
          <ul className="space-y-0.5 typo-mono text-xs text-[color:var(--color-danger)]">
            {column_mapping.missing_required.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      <ColumnMapper
        entityType={entityType}
        validation={validation}
        overrides={overrides}
        onSetMapping={onSetMapping}
        onClearMapping={onClearMapping}
        onFieldCreated={handleFieldCreated}
        onError={onError}
      />

      {validateCsv.isPending && (
        <div className="inline-flex items-center gap-2 typo-body text-[color:var(--color-text-md)]">
          <Loader2 className="h-3 w-3 animate-spin" />
          Re-running validation with updated schema…
        </div>
      )}

      <div className="flex justify-between border-t border-[color:var(--color-border)] pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          disabled={hasBlockingErrors || saving}
          onClick={handleContinue}
          title={
            hasBlockingErrors
              ? "Map all required columns before continuing"
              : undefined
          }
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Continue
        </Button>
      </div>
    </div>
  );
}
